<script>
    import { beforeUpdate, createEventDispatcher, onMount } from 'svelte';
    import ListGrouped from './ListGrouped.svelte';
    import ListUngrouped from './ListUngrouped.svelte';
    import Button from './Button.svelte';
    import {
        isValidObject,
        getObjectProperty,
        textIncludes,
    } from './utils.js';
    import {sgettext} from '../../i18n.js'

    const dispatch = createEventDispatcher();

    // container variables
    export let container = undefined;
    export let skipWindowEventId = null;
    export let selectHeight = 42;
    export let unselectedHeight = 72;
    let containerStyles = `--height: ${selectHeight}px;`;
    $: {
        selectHeight = selectedItem ? unselectedHeight : selectHeight;
        containerStyles = `--height: ${selectHeight}px;`;
    }

    export let showTooltip = true;

    // status flags
    export let isWaiting = false;
    export let hasError = false;

    // input variables
    export let input = undefined;
    export let id = null;
    export let name = null;
    export let isDisabled = false;
    export let readOnly = false;
    export let clearable = false;
    export let searchIcon = false;
    export let inputAttributes = {};
    $: if (!isValidObject(inputAttributes)) inputAttributes = {};
    let inputLeft = 0;
    $: inputLeft = selectedItem && showImages && !listOpen ? 40 : 0;
    export let centerItems = false;

    // list variables
    let list = undefined;
    export let isListGrouped = false;
    export let headers = ['Label', 'Description', 'Items'];
    export let showDescriptions = false;
    export let showImages = true;
    export let noOptionsMessage = sgettext('$$RESULTS_FOUND$$');
    export let listPlacement = 'auto';
    export let listAbsoluteParentId = undefined;
    export let listAbsoluteScrollParentSelector = undefined;
    export let selectedBackgroundColor = '#dd5900';
    export let selectedFontColor = '#fff';
    export let hoverBackgroundColor = '#e9ecef';
    export let hoverFontColor = '#454545';

    /*
    options
    - when isListGrouped = true, option is a group includes items
    - when isListGrouped = false, option is an item
    */
    export let options = [];
    let visibleOptions = [];

    // data getters
    export let itemValueKey = 'value';
    export let getItemValue = item => getObjectProperty(item, itemValueKey);
    export let itemLabelKey = 'label';
    export let getItemLabel = item => getObjectProperty(item, itemLabelKey);
    export let itemDescriptionKey = 'description';
    export let getItemDescription = item => getObjectProperty(item, itemDescriptionKey);
    export let itemImageUrlKey = 'image_url';
    export let getItemImageUrl = item => getObjectProperty(item, itemImageUrlKey);
    export let groupLabelKey = 'label';
    export let getGroupLabel = group => getObjectProperty(group, groupLabelKey);
    export let groupDescriptionKey = 'description';
    export let getGroupDescription = group => getObjectProperty(group, groupDescriptionKey);
    export let groupImageUrlKey = 'image_url';
    export let getGroupImageUrl = group => getObjectProperty(group, groupImageUrlKey);
    export let groupItemsKey = 'items';
    export let getGroupItems = group => getObjectProperty(group, groupItemsKey, []);

    // selected item and value
    export let selectedItem = null;
    export let groupOfSelectedItem = null;
    export let onSelectItem = (event, item) => {}
    export let onClear = event => {}
    let value = null;
    $: value = getItemValue(selectedItem);

    // label and placeholder
    export let placeholder = sgettext('$$SEARCH_OR_SELECT$$')+'...';
    $: showLabel = selectedItem && !listOpen;
    $: text = isListGrouped ? getGroupLabel(groupOfSelectedItem) : getItemLabel(selectedItem);
    $: subtext = isListGrouped ? getItemLabel(selectedItem) : getItemDescription(selectedItem);

    // filter
    export let filterText = '';
    export let itemIncludes = (item, keyword) => {
        if (isListGrouped) {
            return textIncludes(getItemLabel(item), keyword);
        } else {
            if (showDescriptions) {
                return textIncludes(getItemLabel(item), keyword)
                    || textIncludes(getItemDescription(item), keyword);
            } else {
                return textIncludes(getItemLabel(item), keyword);
            }
        }
    };
    export let groupIncludes = (group, keyword) => {
        if (showDescriptions) {
            return textIncludes(getGroupLabel(group), keyword)
                || textIncludes(getGroupDescription(group), keyword);
        } else {
            return textIncludes(getGroupLabel(group), keyword);
        }
    };
    export let optionIncludes = (option, keyword) => {
        if (isListGrouped) {
            if (groupIncludes(option, keyword)) return true;
			for (let item of getGroupItems(option)) {
				if (itemIncludes(item, keyword)) return true;
			}
            return false;
        } else {
            return itemIncludes(option, keyword);
        }
    }
    export let filterMethod = (originalOptions, keyword) => {
        if (!keyword) return originalOptions;

        let result = [];
        for (let option of originalOptions) {
            if (optionIncludes(option, keyword)) {
                result.push(option);
            }
        }
        return result;
    }
    $: visibleOptions = filterMethod(options, filterText);

    // list
    let listOpen = false;
    let toggleList = () => {
        listOpen = !listOpen;
        filterText = '';
    }
    let itemSelected = event => {
        const { detail } = event;
        if (detail) {
            filterText = '';
            selectedItem = Object.assign({}, detail);
            setTimeout(() => {toggleList()});

            onSelectItem(event, selectedItem);
        }
    }

    // button
    let handleButtonClick = event => {
        if (isDisabled || readOnly || isWaiting) return;

        // handle the clear button
        if (clearable && !listOpen && selectedItem) {
            selectedItem = null;
            onClear(event);
        }
    };

    export const loadOptions = undefined;

    export const loadOptionsInterval = 300;

    export let listAutoWidth = false;
    export let containerClasses = '';
    export let listOffset = 5;

    // focus
    export let isFocused = false;
    let handleFocus = () => {
        isFocused = true;
    };
    $: {
        if (input) {
            if (isFocused || listOpen) input.focus();
            else input.blur();
        }
    }

    onMount(() => {
        if (isFocused && input) input.focus();
    });

    let hoverItemIndex;

    beforeUpdate(async () => {

    });

    // handlers
    let handleWindowEvent = event => {
        if (!container) return;
        const eventTarget =
            event.path && event.path.length > 0 ? event.path[0] : event.target;
        if (
            container.contains(eventTarget) ||
            eventTarget.id === skipWindowEventId ||
            list && list.contains(eventTarget)
        )
            return;
        isFocused = false;
        listOpen = false;
        if (input) input.blur();
    }
    let handleClick = (event) => {
        if (isDisabled || isWaiting) return;
        toggleList();
        isFocused = !listOpen;
    }
    let handleKeyDown = e => {
        if (!isFocused) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                listOpen = true;
                break;
            case 'ArrowUp':
                e.preventDefault();
                listOpen = true;
                break;
            case 'Tab':
                if (!listOpen) isFocused = false;
                break;
            case 'Backspace':
                if (filterText.length > 0) return;
                break;
            case 'ArrowLeft':
                if (filterText.length > 0) return;
                break;
            case 'ArrowRight':
                if (filterText.length > 0) return;
                break;
        }
    }
</script>

<svelte:window
    on:click={handleWindowEvent}
    on:focusin={handleWindowEvent}
    on:keydown={handleKeyDown}
/>

<div
    class='select-container {containerClasses}'
    class:hasError
    class:disabled={isDisabled || isWaiting}
    class:focused={isFocused}
    style={`${containerStyles}${centerItems ? 'text-align: center;' : ''}`}
    on:click={handleClick}
    bind:this={container}
    title={showTooltip ? subtext : null}
>
    <div class='wrapper'>
        {#if showImages && selectedItem && !listOpen}
            {#if isListGrouped}
                {#if groupOfSelectedItem}
                    <img
                        src={getGroupImageUrl(groupOfSelectedItem)}
                        alt={getItemLabel(selectedItem)}
                    />
                {/if}
            {:else}
                <img
                    src={getItemImageUrl(selectedItem)}
                    alt={getItemLabel(selectedItem)}
                />
            {/if}
        {/if}

        <div class='label-container'>
            <div class='text'>{#if showLabel}{text}{/if}</div>
            {#if subtext}
                <div class='subtext'>{#if showLabel}{subtext}{/if}</div>
            {/if}
        </div>

        {#if !showLabel}
            <div class='input-container'>
                <input
                    {...inputAttributes}
                    type='text'
                    bind:this={input}
                    on:focus={handleFocus}
                    bind:value={filterText}
                    style='--input-left: {inputLeft}px'
                    placeholder={placeholder}
                    autocapitalize='none'
                    autocomplete='off'
                    autocorrect='off'
                    spellcheck={false}
                    tabindex=0
                    aria-autocomplete='list'
                    readOnly={readOnly || !listOpen}
                    disabled={isDisabled || isWaiting}
                />
            </div>
        {/if}

        {#if !isDisabled && !readOnly}
            <Button
                showSpinner={isWaiting}
                showTimes={clearable && !isWaiting && !listOpen && selectedItem}
                showDropUp={!isWaiting && listOpen}
                showSearch={searchIcon}
                on:click={handleButtonClick}
            />
        {/if}
    </div>

    {#if listOpen}
        {#if isListGrouped}
            <ListGrouped
                bind:container={list}
                parent={container}
                options={visibleOptions}
                bind:listOpen
                {headers}
                {selectedItem}
                bind:groupOfSelectedItem={groupOfSelectedItem}
                {getItemValue}
                {getItemLabel}
                {getGroupLabel}
                {getGroupDescription}
                {getGroupImageUrl}
                {getGroupItems}
                {filterText}
                {showImages}
                {noOptionsMessage}
                {showTooltip}
                placement={listPlacement}
                autoWidth={listAutoWidth}
                offset={listOffset}
                absoluteParentId={listAbsoluteParentId}
                absoluteScrollParentSelector={listAbsoluteScrollParentSelector}
                {selectedBackgroundColor}
                {selectedFontColor}
                {hoverBackgroundColor}
                {hoverFontColor}
                bind:hoverItemIndex
                on:itemSelected={itemSelected}
                on:closeList={toggleList}
            />
        {:else}
            <ListUngrouped
                bind:container={list}
                parent={container}
                options={visibleOptions}
                {selectedItem}
                {getItemValue}
                {getItemLabel}
                {getItemDescription}
                {getItemImageUrl}
                {filterText}
                {showDescriptions}
                {showImages}
                {noOptionsMessage}
                {showTooltip}
                placement={listPlacement}
                autoWidth={listAutoWidth}
                {centerItems}
                offset={listOffset}
                {selectedBackgroundColor}
                {selectedFontColor}
                {hoverBackgroundColor}
                {hoverFontColor}
                bind:hoverItemIndex
                on:itemSelected={itemSelected}
                on:closeList={toggleList}
            />
        {/if}
    {/if}

    <input
        id={id}
        name={name}
        type='hidden'
        value={value}
    />
</div>

<style>
    .select-container {
        border: 1px solid #d8dbdf;
        border-radius: 5px;
        box-sizing: border-box;
        height: var(--height);
        position: relative;
        display: grid;
        align-items: center;
        background: #fff;
        padding: 0;
        margin: 0;
    }

    .wrapper {
        overflow: hidden;
        display: flex;
    }

    .wrapper img {
        flex: 0 0 50px;
        margin: auto 10px;
        width: 50px;
        height: 50px;
    }

    .label-container {
        flex: 1 1 200px;
        margin: auto 0;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    .label-container .text {
        font-weight: bold;
        font-size: medium;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    .label-container .subtext {
        font-weight: normal;
        font-size: small;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    .input-container {
        display: block;
        position: absolute;
        height: 40px;
        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    .input-container input {
        cursor: default;
        border: none;
        color: #3f4f5f;
        height: 40px;
        padding: 0 20px 0 10px;
        width: 100%;
        background: transparent;
        font-size: 18px;
        letter-spacing: -0.08px;
        left: 10px;
        margin: 0;
    }

    .select-container input::placeholder {
        color: #78848f;
        opacity: 1;
    }

    .select-container input:focus {
        outline: none;
    }

    .select-container:hover {
        border-color: #b2b8bf;
    }

    .select-container.focused {
        border-color: #006fe8;
    }

    .select-container.disabled {
        background: #ebedef;
        border-color: #ebedef;
        color: #c1c6cc;
    }

    .select-container.disabled input::placeholder {
        color: #c1c6cc;
        opacity: 1;
    }

    img {
        width: 30px;
        height: 30px;
    }

    .hasError {
        border: 1px solid #ff2d55;
        background: #fff;
    }

    .a11yText {
        z-index: 9999;
        border: 0px;
        clip: rect(1px, 1px, 1px, 1px);
        height: 1px;
        width: 1px;
        position: absolute;
        overflow: hidden;
        padding: 0px;
        white-space: nowrap;
    }

    @keyframes rotate {
        100% {
            transform: rotate(360deg);
        }
    }
</style>
